import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FaChartBar,
  FaCalendarAlt,
  FaCalendarWeek,
  FaCalendarDay,
  FaSpinner,
  FaPlay,
  FaUndo,
  FaFilter,
  FaChartLine
} from "react-icons/fa";
import './ComparaisonDate.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ComparaisonDate = () => {
  const [revenues, setRevenues] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthComparisonYear, setMonthComparisonYear] = useState(new Date().getFullYear());
  const [weekComparisonYear, setWeekComparisonYear] = useState(new Date().getFullYear());
  const [totalRevenue, setTotalRevenue] = useState({});
  const [weeklyRevenue, setWeeklyRevenue] = useState({});
  const [monthlyRevenue, setMonthlyRevenue] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');

  useEffect(() => {
    const fetchRevenues = async () => {
      try {
        setLoading(true);
        const commandesSnapshot = await getDocs(collection(db, "total_commande"));
        const commandes = commandesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRevenues(commandes);
      } catch (error) {
        console.error('Erreur lors du chargement des revenus:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRevenues();
  }, []);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    let monthsToShow = 12;
    if (selectedYear === currentYear) {
      monthsToShow = currentMonth + 1;
    } else if (selectedYear > currentYear) {
      monthsToShow = 1;
    }

    if (selectedMonth >= monthsToShow) {
      setSelectedMonth(monthsToShow - 1);
    }
  }, [selectedYear, selectedMonth]);

  const handleDaySelect = (date) => {
    if (selectedDays.includes(date)) {
      setSelectedDays(selectedDays.filter((day) => day !== date));
    } else {
      setSelectedDays([...selectedDays, date]);
    }
  };

  const handleWeekSelect = (week) => {
    if (selectedWeeks.includes(week)) {
      setSelectedWeeks(selectedWeeks.filter((w) => w !== week));
    } else {
      setSelectedWeeks([...selectedWeeks, week]);
    }
  };

  const handleMonthSelect = (month) => {
    if (selectedMonths.includes(month)) {
      setSelectedMonths(selectedMonths.filter((m) => m !== month));
    } else {
      setSelectedMonths([...selectedMonths, month]);
    }
  };

  const calculateTotalRevenue = () => {
    // Trier les dates sélectionnées
    const sortedDays = [...selectedDays].sort((a, b) => new Date(a) - new Date(b));
  
    const totals = sortedDays.map((day) => {
      const revenueForDay = revenues.filter((commande) => {
        const orderDate = new Date(commande.date.toDate());
        return orderDate.toLocaleDateString() === day;
      }).reduce((acc, curr) => acc + curr.total, 0);
      return revenueForDay;
    });
  
    const percentageChanges = totals.map((current, index) => {
      if (index === 0) return null; // Pas de précédent pour le premier jour
      return calculatePercentageChange(current, totals[index - 1]);
    });
  
    const labels = sortedDays;
    const data = totals;
  
    setTotalRevenue({
      labels,
      data,
      percentageChanges,
    });
  };  

  const calculateWeeklyRevenue = () => {
    // Trier les semaines par ordre croissant
    const sortedWeeks = [...selectedWeeks].sort((a, b) => a - b);
  
    const totals = sortedWeeks.map((week) => {
      const revenueForWeek = revenues.filter((commande) => {
        const orderDate = new Date(commande.date.toDate());
        const orderWeek = getWeekNumber(orderDate);
        const orderYear = orderDate.getFullYear();
        return orderWeek === week && orderYear === weekComparisonYear;
      }).reduce((acc, curr) => acc + curr.total, 0);
      return revenueForWeek;
    });
  
    const percentageChanges = totals.map((current, index) => {
      if (index === 0) return null; // Pas de précédent pour la première semaine
      return calculatePercentageChange(current, totals[index - 1]);
    });
  
    const labels = sortedWeeks.map((week) => `Semaine ${week} de ${weekComparisonYear}`);
    const data = totals;
  
    setWeeklyRevenue({
      labels,
      data,
      percentageChanges,
    });
  };  

  const calculateMonthlyRevenue = () => {
    // Trier les mois par ordre croissant
    const sortedMonths = [...selectedMonths].sort((a, b) => a - b);
  
    const totals = sortedMonths.map((month) => {
      const revenueForMonth = revenues.filter((commande) => {
        const orderDate = new Date(commande.date.toDate());
        return (
          orderDate.getMonth() === month &&
          orderDate.getFullYear() === monthComparisonYear
        );
      }).reduce((acc, curr) => acc + curr.total, 0);
      return revenueForMonth;
    });
  
    const percentageChanges = totals.map((current, index) => {
      if (index === 0) return null; // Pas de précédent pour le premier mois
      return calculatePercentageChange(current, totals[index - 1]);
    });
  
    const labels = sortedMonths.map((month) =>
      `${new Date(0, month).toLocaleString("default", { month: "long" })} de ${monthComparisonYear}`
    );
    const data = totals;
  
    setMonthlyRevenue({
      labels,
      data,
      percentageChanges,
    });
  };  

  const handleMonthChange = (event) => {
    setSelectedMonth(parseInt(event.target.value, 10));
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

  const handleMonthComparisonYearChange = (event) => {
    setMonthComparisonYear(parseInt(event.target.value, 10));
  };

  const handleWeekComparisonYearChange = (event) => {
    setWeekComparisonYear(parseInt(event.target.value, 10));
  };  

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / (1000 * 60 * 60 * 24);
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const renderCalendar = () => {
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const calendarDays = [];

    // Calculer le nombre correct de jours vides
    const emptyDays = firstDayOfMonth === 0 ? 0 : firstDayOfMonth;
    
    // Ajouter les jours vides avant le premier jour du mois
    for (let i = 0; i < emptyDays; i++) {
      calendarDays.push(<div key={`empty-${i}`} className="comparaison-date-calendar-day-empty"></div>);
    }

    // Ajouter les jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      const formattedDate = new Date(selectedYear, selectedMonth, day).toLocaleDateString();
      calendarDays.push(
        <button
          key={formattedDate}
          className={`comparaison-date-calendar-day ${selectedDays.includes(formattedDate) ? "comparaison-date-calendar-day-selected" : ""}`}
          onClick={() => handleDaySelect(formattedDate)}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="comparaison-date-calendar-grid">
        <div className="comparaison-date-calendar-header">
          <span className="comparaison-date-calendar-header-day">Dim</span>
          <span className="comparaison-date-calendar-header-day">Lun</span>
          <span className="comparaison-date-calendar-header-day">Mar</span>
          <span className="comparaison-date-calendar-header-day">Mer</span>
          <span className="comparaison-date-calendar-header-day">Jeu</span>
          <span className="comparaison-date-calendar-header-day">Ven</span>
          <span className="comparaison-date-calendar-header-day">Sam</span>
        </div>
        <div className="comparaison-date-calendar-days">
          {calendarDays}
        </div>
      </div>
    );
  };

  const renderWeeklyCalendar = () => {
    const totalWeeks = 52; // Nombre total de semaines dans une année
    const weekButtons = [];
    for (let week = 1; week <= totalWeeks; week++) {
      weekButtons.push(
        <button
          key={week}
          className={`comparaison-date-week-btn ${selectedWeeks.includes(week) ? "comparaison-date-week-btn-selected" : ""}`}
          onClick={() => handleWeekSelect(week)}
        >
          Semaine {week}
        </button>
      );
    }
    return <div className="comparaison-date-weeks-grid">{weekButtons}</div>;
  };  

  const renderMonthlyCalendar = () => {
    const monthButtons = [];
    for (let month = 0; month < 12; month++) {
      monthButtons.push(
        <button
          key={month}
          className={`comparaison-date-month-btn ${selectedMonths.includes(month) ? "comparaison-date-month-btn-selected" : ""}`}
          onClick={() => handleMonthSelect(month)}
        >
          {new Date(0, month).toLocaleString("default", { month: "long" })}
        </button>
      );
    }
    return <div className="comparaison-date-months-grid">{monthButtons}</div>;
  };

  const renderMonthSelector = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Détermine le nombre de mois à afficher
    let monthsToShow = 12;
    if (selectedYear === currentYear) {
      monthsToShow = currentMonth + 1;
    } else if (selectedYear > currentYear) {
      monthsToShow = 1;
    }

    return (
      <div className="comparaison-date-selector-row">
        <div className="comparaison-date-selector-group">
          <label className="comparaison-date-selector-label">Mois</label>
          <select 
            value={selectedMonth} 
            onChange={handleMonthChange}
            className="comparaison-date-selector-select"
          >
            {Array.from({ length: monthsToShow }, (_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>
        </div>
        <div className="comparaison-date-selector-group">
          <label className="comparaison-date-selector-label">Année</label>
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="comparaison-date-selector-select"
          >
            {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderWeekComparisonYearSelector = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2023 }, (_, i) => currentYear - i);

    return (
      <div className="comparaison-date-year-selector-group">
        <label className="comparaison-date-year-selector-label">Année</label>
        <select
          value={weekComparisonYear}
          onChange={handleWeekComparisonYearChange}
          className="comparaison-date-year-selector-select"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderMonthComparisonYearSelector = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2023 }, (_, i) => currentYear - i);

    return (
      <div className="comparaison-date-year-selector-group">
        <label className="comparaison-date-year-selector-label">Année</label>
        <select
          value={monthComparisonYear}
          onChange={handleMonthComparisonYearChange}
          className="comparaison-date-year-selector-select"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    );
  };  

  const getChartOptions = (data) => {
    const isMobile = window.innerWidth < 768;
    const isSmallMobile = window.innerWidth < 480;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: {
              size: isSmallMobile ? 10 : 12,
              weight: '600',
            },
            color: '#1f2937',
            padding: isSmallMobile ? 8 : 15,
          },
        },
        title: {
          display: true,
          text: "Comparaison des Revenus",
          font: {
            size: isSmallMobile ? 14 : 16,
            weight: '600',
          },
          color: '#1f2937',
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: isSmallMobile ? 8 : 12,
          borderWidth: 1,
          callbacks: {
            label: (tooltipItem) => {
              const index = tooltipItem.dataIndex;
              const percentage = data.percentageChanges[index];
              const percentageText = percentage !== null
                ? ` (${percentage.toFixed(1)}%)`
                : "";
              return `Revenu : ${tooltipItem.raw} €${percentageText}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            font: {
              size: isSmallMobile ? 10 : 11,
              weight: '500',
            },
            color: '#6b7280',
            maxTicksLimit: isMobile ? 5 : 8,
          },
          grid: {
            color: '#f3f4f6',
            borderDash: [5, 5],
          },
        },
        x: {
          ticks: {
            font: {
              size: isSmallMobile ? 8 : isMobile ? 9 : 10,
              weight: '500',
            },
            color: '#6b7280',
            maxRotation: isSmallMobile ? 90 : isMobile ? 60 : 45,
            maxTicksLimit: isSmallMobile ? 3 : isMobile ? 4 : undefined,
          },
          grid: {
            display: false,
          },
        },
      },
    };
  };

  const createComparisonChart = (data) => {
    const maxRevenue = Math.max(...data.data);
    const maxLimit = maxRevenue * 1.1; // Ajoute 10% de marge
  
    return (
      <div className="comparaison-chart-container">
        <Bar
          data={{
            labels: data.labels,
            datasets: [
              {
                label: "Revenus",
                data: data.data,
                backgroundColor: "rgba(59, 130, 246, 0.2)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 2,
                borderRadius: 4,
                borderSkipped: false,
              },
            ],
          }}
          options={{
            ...getChartOptions(data),
            scales: {
              ...getChartOptions(data).scales,
              y: {
                ...getChartOptions(data).scales.y,
                max: maxLimit,
              },
            },
          }}
        />
      </div>
    );
  };  

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100; // Si précédent est 0, renvoie 100% ou 0%
    return ((current - previous) / previous) * 100;
  };  

  const resetDaySelection = () => {
    setSelectedDays([]);
    setTotalRevenue({});
  };
  
  const resetWeekSelection = () => {
    setSelectedWeeks([]);
    setWeeklyRevenue({});
  };
  
  const resetMonthSelection = () => {
    setSelectedMonths([]);
    setMonthlyRevenue({});
  };  

  if (loading) {
    return (
      <div className="comparaison-date-container">
        <div className="comparaison-date-loading">
          <div className="comparaison-date-spinner">
            <FaSpinner className="spinning" />
          </div>
          <p className="comparaison-date-loading-text">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comparaison-date-container">
      {/* Header */}
      <div className="comparaison-date-header">
        <div className="comparaison-date-title-section">
          <FaChartLine className="comparaison-date-title-icon" />
          <h1 className="comparaison-date-title">Comparaison des Revenus</h1>
        </div>
        <p className="comparaison-date-subtitle">
          Analysez et comparez vos revenus par jour, semaine ou mois
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="comparaison-date-tabs">
        <button
          className={`comparaison-date-tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          <FaCalendarDay className="comparaison-date-tab-icon" />
          <span className="comparaison-date-tab-text">Quotidien</span>
        </button>
        <button
          className={`comparaison-date-tab ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          <FaCalendarWeek className="comparaison-date-tab-icon" />
          <span className="comparaison-date-tab-text">Hebdomadaire</span>
        </button>
        <button
          className={`comparaison-date-tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          <FaCalendarAlt className="comparaison-date-tab-icon" />
          <span className="comparaison-date-tab-text">Mensuel</span>
        </button>
      </div>

      {/* Daily Tab */}
      {activeTab === 'daily' && (
        <div className="comparaison-date-tab-content">
          <div className="comparaison-date-section">
            <div className="comparaison-date-section-header">
              <FaCalendarDay className="comparaison-date-section-icon" />
              <h2 className="comparaison-date-section-title">Comparaison Quotidienne</h2>
            </div>
            
            <div className="comparaison-date-selector-card">
              <div className="comparaison-date-selector-header">
                <FaFilter className="comparaison-date-selector-icon" />
                <h3 className="comparaison-date-selector-title">Sélectionner la Période</h3>
              </div>
              {renderMonthSelector()}
            </div>

            <div className="comparaison-date-calendar-card">
              <div className="comparaison-date-calendar-header">
                <h3 className="comparaison-date-calendar-title">
                  Calendrier de {new Date(selectedYear, selectedMonth).toLocaleString("default", {
                    month: "long",
                  })}{" "}
                  {selectedYear}
                </h3>
                <div className="comparaison-date-selected-count">
                  {selectedDays.length} jour{selectedDays.length > 1 ? 's' : ''} sélectionné{selectedDays.length > 1 ? 's' : ''}
                </div>
              </div>
              <div className="comparaison-date-calendar-wrapper">
                {renderCalendar()}
              </div>
            </div>

            {selectedDays.length > 0 && (
              <div className="comparaison-date-actions">
                <button onClick={calculateTotalRevenue} className="comparaison-date-compare-btn">
                  <FaPlay className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Comparer par Jour</span>
                </button>
                <button onClick={resetDaySelection} className="comparaison-date-reset-btn">
                  <FaUndo className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Réinitialiser</span>
                </button>
              </div>
            )}

            {totalRevenue.labels && (
              <div className="comparaison-date-chart-card">
                <div className="comparaison-date-chart-header">
                  <FaChartBar className="comparaison-date-chart-icon" />
                  <h3 className="comparaison-date-chart-title">Graphique de Comparaison</h3>
                </div>
                {createComparisonChart(totalRevenue)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Weekly Tab */}
      {activeTab === 'weekly' && (
        <div className="comparaison-date-tab-content">
          <div className="comparaison-date-section">
            <div className="comparaison-date-section-header">
              <FaCalendarWeek className="comparaison-date-section-icon" />
              <h2 className="comparaison-date-section-title">Comparaison Hebdomadaire</h2>
            </div>

            <div className="comparaison-date-calendar-card">
              <div className="comparaison-date-calendar-header">
                <h3 className="comparaison-date-calendar-title">Calendrier Hebdomadaire</h3>
                <div className="comparaison-date-selected-count">
                  {selectedWeeks.length} semaine{selectedWeeks.length > 1 ? 's' : ''} sélectionnée{selectedWeeks.length > 1 ? 's' : ''}
                </div>
              </div>
              <div className="comparaison-date-year-selector">
                {renderWeekComparisonYearSelector()}
              </div>
              <div className="comparaison-date-weeks-wrapper">
                {renderWeeklyCalendar()}
              </div>
            </div>

            {selectedWeeks.length > 0 && (
              <div className="comparaison-date-actions">
                <button onClick={calculateWeeklyRevenue} className="comparaison-date-compare-btn">
                  <FaPlay className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Comparer par Semaine</span>
                </button>
                <button onClick={resetWeekSelection} className="comparaison-date-reset-btn">
                  <FaUndo className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Réinitialiser</span>
                </button>
              </div>
            )}

            {weeklyRevenue.labels && (
              <div className="comparaison-date-chart-card">
                <div className="comparaison-date-chart-header">
                  <FaChartBar className="comparaison-date-chart-icon" />
                  <h3 className="comparaison-date-chart-title">Graphique de Comparaison</h3>
                </div>
                {createComparisonChart(weeklyRevenue)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly Tab */}
      {activeTab === 'monthly' && (
        <div className="comparaison-date-tab-content">
          <div className="comparaison-date-section">
            <div className="comparaison-date-section-header">
              <FaCalendarAlt className="comparaison-date-section-icon" />
              <h2 className="comparaison-date-section-title">Comparaison Mensuelle</h2>
            </div>

            <div className="comparaison-date-calendar-card">
              <div className="comparaison-date-calendar-header">
                <h3 className="comparaison-date-calendar-title">Calendrier Mensuel</h3>
                <div className="comparaison-date-selected-count">
                  {selectedMonths.length} mois sélectionné{selectedMonths.length > 1 ? 's' : ''}
                </div>
              </div>
              <div className="comparaison-date-year-selector">
                {renderMonthComparisonYearSelector()}
              </div>
              <div className="comparaison-date-months-wrapper">
                {renderMonthlyCalendar()}
              </div>
            </div>

            {selectedMonths.length > 0 && (
              <div className="comparaison-date-actions">
                <button onClick={calculateMonthlyRevenue} className="comparaison-date-compare-btn">
                  <FaPlay className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Comparer par Mois</span>
                </button>
                <button onClick={resetMonthSelection} className="comparaison-date-reset-btn">
                  <FaUndo className="comparaison-date-btn-icon" />
                  <span className="comparaison-date-btn-text">Réinitialiser</span>
                </button>
              </div>
            )}

            {monthlyRevenue.labels && (
              <div className="comparaison-date-chart-card">
                <div className="comparaison-date-chart-header">
                  <FaChartBar className="comparaison-date-chart-icon" />
                  <h3 className="comparaison-date-chart-title">Graphique de Comparaison</h3>
                </div>
                {createComparisonChart(monthlyRevenue)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparaisonDate;

